import React, { useMemo, useState } from "react";
import { Button, Form, Modal, Table } from "react-bootstrap";
import Spinner1 from "../common/spinners";
import { truncateText } from "../../utils/utilities";
import moment from "moment";
import LabRequestHistoryModal from "../modals/LabRequestHistoryModal";
import { FileHeart, FullscreenIcon } from "lucide-react";

const MedicalReport = ({ data = [], loading = false }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedData, setSelectedData] = useState();
  const [selectedLab, setSelectedLab] = useState(null);
  const [show, setShow] = useState(false);
  const itemsPerPage = 5;

  const filteredData = useMemo(
    () =>
      (Array.isArray(data) ? data : []).filter((lab) =>
        lab?.item?.labTest?.name?.toLowerCase()?.includes(searchTerm.toLowerCase())
      ),
    [data, searchTerm]
  );

  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
  const currentItems = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const openModal = (lab) => {
    setSelectedLab(lab);
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
    setSelectedLab(null);
  };

  return (
    <div className="w-100">
      <div className="data-panel-head">
        <div className="data-panel-copy">
          <h5>Medical reports</h5>
          <p>Review diagnostic reports, specimen details, and report costs from a calmer history view.</p>
        </div>
        <div className="data-panel-actions">
          <div className="data-search">
            <Form.Control
              type="text"
              placeholder="Search report title"
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <div className="data-table-shell">
        {loading ? (
          <div className="w-100 d-flex justify-content-center py-5">
            <Spinner1 />
          </div>
        ) : currentItems.length === 0 ? (
          <div className="data-table-empty">
            <h6>No medical reports found</h6>
            <p>Reports will appear here when they are uploaded or generated for your account.</p>
          </div>
        ) : (
          <div className="data-table-wrap">
            <table className="table data-table align-middle w-100">
              <thead>
                <tr>
                  <th>Medical Report</th>
                  <th>Specimen</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Open</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <div className="d-flex align-items-center gap-3">
                        <span className="data-icon-avatar">
                          <FileHeart size={18} />
                        </span>
                        <div>
                          <div className="data-cell-title text-capitalize">
                            {truncateText(`${item?.item?.labTest?.name}`, 40)}
                          </div>
                          <div className="data-cell-meta">
                            {moment(item?.createdAt).format("dddd, Do MMMM, YYYY")}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="data-cell-title text-capitalize">
                        {item?.item?.labTest?.specimens?.map((specimen) => specimen.label).join(", ") || "---"}
                      </div>
                      <div className="data-cell-meta">
                        {moment(item?.item?.labTest?.createdAt).format("dddd, Do MMM YYYY")}
                      </div>
                    </td>
                    <td>
                      <div className="data-cell-title">
                        {item?.item?.transaction?.amount?.toLocaleString("en-NG", {
                          style: "currency",
                          currency: "NGN",
                        })}
                      </div>
                      <div className="data-cell-meta">
                        {item?.item?.transaction?.description
                          ? truncateText(`${item?.item?.transaction?.description}`, 20)
                          : "No description"}
                      </div>
                    </td>
                    <td>
                      <span className={`data-status-badge ${item?.status === 0 ? "is-warning" : "is-success"}`}>
                        {item?.status === 0 ? "Awaiting Payment" : "Completed"}
                      </span>
                    </td>
                    <td>
                      {item?.status === 1 ? (
                        <button type="button" className="data-action-btn" onClick={() => openModal(item)}>
                          <FullscreenIcon size={17} />
                        </button>
                      ) : (
                        <span className="data-cell-meta">Pending</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredData.length > 0 && (
          <div className="data-pagination">
            <Button variant="light" disabled={currentPage === 1} onClick={() => setCurrentPage((page) => page - 1)}>
              Previous
            </Button>
            <span>Page {currentPage} of {totalPages}</span>
            <Button
              variant="light"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((page) => page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      <Modal size="lg" show={show} onHide={handleClose} scrollable centered>
        <Modal.Header closeButton />
        <Modal.Body>
          {selectedLab ? (
            <div className="w-100">
              <ul style={{ listStyleType: "none", padding: 0 }}>
                <li className="mb-4">
                  Title: <span>{selectedLab?.item?.labTest?.name}</span>
                </li>
                <Table>
                  <thead>
                    <tr>
                      <th>Parameter</th>
                      <th>Value</th>
                      <th>Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedLab?.item?.parameters?.map((x) => (
                      <tr key={x?.id}>
                        <td>{x?.name}</td>
                        <td>{x?.value}</td>
                        <td>{x?.reference}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ul>
            </div>
          ) : (
            "No data available"
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <LabRequestHistoryModal
        show={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        data={selectedData}
      />
    </div>
  );
};

export default MedicalReport;
